import {
  KeyIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  v2ApiKeysList,
  v2ApiKeysCreate,
  v2ApiKeysRevoke,
} from "../services/tradingAgentsV2";

export default function ApiKeys() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [rateLimit, setRateLimit] = useState("100");
  const [expiresDays, setExpiresDays] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["api-keys"],
    queryFn: v2ApiKeysList,
  });

  const createMutation = useMutation({
    mutationFn: v2ApiKeysCreate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      setNewKey(data.api_key);
      setName("");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: v2ApiKeysRevoke,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["api-keys"] }),
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({
      name: name.trim(),
      rate_limit: Number(rateLimit),
      expires_days: expiresDays ? Number(expiresDays) : undefined,
    });
  };

  const keys = query.data?.api_keys ?? [];

  return (
    <div className="space-y-6">
      {newKey && (
        <div className="card border-2 border-yellow-300 bg-yellow-50">
          <div className="flex items-start gap-3">
            <KeyIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-yellow-600" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-800">
                API Key Created
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                Copy this key now — it will not be shown again.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 rounded bg-white px-3 py-2 font-mono text-sm text-gray-900 shadow-sm">
                  {newKey}
                </code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(newKey)}
                  className="rounded p-2 text-gray-600 hover:bg-gray-100"
                  title="Copy"
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => setNewKey(null)}
                className="mt-2 text-xs text-yellow-600 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card lg:col-span-1">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Generate API Key
          </h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <Input
              label="Key Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My App"
              required
            />
            <Input
              label="Rate Limit (req/min)"
              type="number"
              min="1"
              value={rateLimit}
              onChange={(e) => setRateLimit(e.target.value)}
            />
            <Input
              label="Expires (days, blank = never)"
              type="number"
              min="1"
              value={expiresDays}
              onChange={(e) => setExpiresDays(e.target.value)}
            />
            {createMutation.isError && (
              <p className="text-sm text-red-600">
                {(createMutation.error as Error).message}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              isLoading={createMutation.isPending}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Generate Key
            </Button>
          </form>
        </div>

        <div className="card lg:col-span-2">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Your API Keys
          </h2>
          {keys.length === 0 ? (
            <p className="text-sm text-gray-500">
              No API keys yet. Generate one to integrate programmatically.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {["Name", "Prefix", "Rate Limit", "Requests", "Status", "Created", ""].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left font-medium text-gray-700"
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {keys.map((k) => (
                    <tr key={k.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {k.name}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">
                        {k.key_prefix}...
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {k.rate_limit}/min
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {k.total_requests.toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            k.is_active
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {k.is_active ? "Active" : "Revoked"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {k.created_at
                          ? new Date(k.created_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {k.is_active && (
                          <button
                            type="button"
                            onClick={() => revokeMutation.mutate(k.id)}
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            title="Revoke"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
